import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaFacebook, FaLinkedin, FaWeixin } from 'react-icons/fa';

const ContactSection = styled.section`
  padding: 5rem 2rem 4rem;
  background: ${({ theme }) => theme.gradients.dark};
  color: ${({ theme }) => theme.colors.white};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.4), transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 300px;
    background: radial-gradient(ellipse, rgba(99, 102, 241, 0.08), transparent 70%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.white};
`;

const SectionSubtitle = styled(motion.p)`
  color: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  margin-bottom: 2.5rem;
`;

const LinksGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const LinkItem = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LinkAnchor = styled.a`
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.3s;
  letter-spacing: 0.3px;

  &:hover {
    color: ${({ theme }) => theme.colors.accentSoft};
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  color: ${({ theme }) => theme.colors.accentSoft};
  transition: all 0.3s ease;

  ${LinkAnchor}:hover & {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.2);
  }
`;

const Footer = styled(motion.div)`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.8rem;
`;

const Contact: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <ContactSection ref={ref}>
      <Container
        as={motion.div}
        variants={containerVariants}
        initial={reduceMotion ? false : 'hidden'}
        animate={reduceMotion || inView ? 'visible' : 'hidden'}
      >
        <SectionTitle variants={itemVariants}>
          Let&apos;s build what&apos;s next.
        </SectionTitle>
        <SectionSubtitle variants={itemVariants}>
          Product, AI, and growth—from idea to launch.
        </SectionSubtitle>

        <LinksGrid>
          <LinkItem variants={itemVariants}>
            <LinkAnchor href="https://facebook.com/ben8987" target="_blank" rel="noopener noreferrer">
              <IconWrapper><FaFacebook /></IconWrapper>
              Facebook
            </LinkAnchor>
          </LinkItem>
          <LinkItem variants={itemVariants}>
            <LinkAnchor href="https://linkedin.com/in/bennz/" target="_blank" rel="noopener noreferrer">
              <IconWrapper><FaLinkedin /></IconWrapper>
              LinkedIn
            </LinkAnchor>
          </LinkItem>
          <LinkItem variants={itemVariants}>
            <LinkAnchor href="#wechat" onClick={(e) => {
              e.preventDefault();
              alert('WeChat ID: ben87nz');
            }}>
              <IconWrapper><FaWeixin /></IconWrapper>
              WeChat
            </LinkAnchor>
          </LinkItem>
        </LinksGrid>

        <Footer variants={itemVariants}>
          © GENAI Technologies
        </Footer>
      </Container>
    </ContactSection>
  );
};

export default Contact;
